// O CLIENTE DO ROTEIRIZADOR.
//
// O backend tem `POST /roteirizar` desde ontem: ele recebe um texto e devolve um documento v2
// montado, mais um prompt de imagem por quadro. Nenhuma tela chamava essa rota — o levantamento
// registrou isso como "tem porta, não tem botão", e uma ferramenta que ninguém alcança não
// atende pedido nenhum.
//
// ── POR QUE O ROTEIRO E A IMAGEM SÃO DUAS CHAMADAS ────────────────────────────────────────────
//
// `/roteirizar` não gera imagem, e isso é decisão do backend, não limitação. Montar a estrutura
// da história é instantâneo; desenhar seis quadros leva minutos. Se fosse uma chamada só, a
// criança olharia para uma tela girando por dois minutos e não veria nada. Separadas, a estrutura
// aparece em segundos — os quadros, os balões, a divisão da página — e cada desenho chega no
// lugar dele conforme fica pronto.

import { auth } from "./firebase.class";
import { Categoria, DocumentoHQ, FormatoPagina } from "./documento.types";

export interface PedidoDeRoteiro {
  texto: string;
  titulo?: string;
  /** T22 · a tag de estilo da história (`api/estilos.ts`). */
  estilo?: string;
  /**
   * QUEM É O PERSONAGEM, nas palavras de quem escreveu — `T12.7`.
   *
   * Vai para dentro do pedido de desenho de TODO quadro, na frente das aparências que a máquina
   * inferiu das cenas. É o que faz o mesmo personagem atravessar os quadros sem depender do
   * modelo: semente igual com pedido diferente devolve desenho diferente, e o pedido de cada
   * quadro é a cena dele.
   *
   * O backend corta em 200 caracteres e passa o campo pela MESMA barreira de conteúdo do texto da
   * história — um campo que chega ao desenhador sem passar por ela seria porta de serviço.
   */
  personagem?: string;
  formato?: FormatoPagina;
  categoria?: Categoria;
  autor?: { id?: string; nome?: string; turma?: string };
}

/** T19 · um recurso de texto do quadro: o recordatório, um balão de fala ou de pensamento. */
export interface Recurso {
  tipo: "recordatorio" | "fala" | "pensamento";
  texto: string;
  quem?: string;
}

/** T19 · um quadro do planejamento. */
export interface QuadroPlanejado {
  numero: number;
  tira: number;
  posicao: number;
  papel: "começo" | "meio" | "fim";
  acontece: string;
  quem: string[];
  enquadramento: string;
  recursos: Recurso[];
  pausa?: boolean;
}

/** T19 · o planejamento da história, que vem antes do canvas. */
export interface PlanoDeQuadrinho {
  tiras: number;
  quadrosPorTira: number;
  frases: number;
  quadros: QuadroPlanejado[];
  porque: string;
}

export interface RespostaDeRoteiro {
  documento: DocumentoHQ;
  /** Um prompt de imagem por quadro, na ordem em que os quadros aparecem no documento. */
  // `cena` (T21): só o que acontece e o enquadramento, sem a descrição — o que vai ao klein.
  promptsPorQuadro: { paginaId: string; quadroId: string; prompt: string; cena?: string; quem?: string[] }[];
  avisos: string[];
  /** T26.R4 · quem aparece na história, com a aparência. Servidor antigo não manda. */
  elenco?: { nome: string; aparencia: string }[];
  /** T19 · o planejamento. Servidor antigo não manda, e aí a tela desenha direto. */
  plano?: PlanoDeQuadrinho;
}

/** Erro que a barreira de conteúdo produziu — distinto de erro de rede ou de servidor. */
export class ConteudoRecusadoError extends Error {
  readonly recusado = true;
}

export async function roteirizar(pedido: PedidoDeRoteiro): Promise<RespostaDeRoteiro> {
  const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;

  const resposta = await fetch(`${process.env.REACT_APP_BACKEND_ROUTE}/roteirizar`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    },
    body: JSON.stringify(pedido),
  });

  const corpo = await resposta.json().catch(() => ({}));

  // 422 é a barreira, e ela precisa de tratamento PRÓPRIO. Se cair no mesmo caminho de "erro,
  // tente de novo", a criança tenta de novo — e a única coisa que muda é que ela tenta a mesma
  // história recusada mais três vezes antes de desistir sem entender.
  if (resposta.status === 422) {
    throw new ConteudoRecusadoError(
      corpo.message || "Essa história não pode ser criada aqui."
    );
  }
  if (!resposta.ok) {
    throw new Error(corpo.error || `Não consegui montar o roteiro (HTTP ${resposta.status}).`);
  }
  return corpo as RespostaDeRoteiro;
}
